import { useNavigate } from "react-router-dom";
import { Eye, LogOut } from "lucide-react";
import { useMe, useStopImpersonation } from "@/hooks/useAuth";

/**
 * Sticky banner shown whenever an admin is impersonating ("logged in as") a
 * user. Makes the impersonation obvious and offers one-click exit back to the
 * admin's own session.
 */
export function ImpersonationBanner() {
  const me = useMe();
  const stop = useStopImpersonation();
  const nav = useNavigate();
  if (!me.data?.impersonating) return null;

  async function exit() {
    try {
      await stop.mutateAsync();
    } finally {
      // Root redirect lands admins on /admin and managers on /manager.
      nav("/", { replace: true });
      // hard reload so every cached view re-fetches as the staff member
      setTimeout(() => window.location.assign("/"), 50);
    }
  }

  return (
    <div role="status" className="sticky top-0 z-60 border-b border-warning/35 bg-bg-elevated shadow-soft">
      <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 bg-warning/[0.12] px-4 py-2 text-[13px]">
        <span className="flex min-w-0 items-center gap-2.5">
          <span className="grid h-6 w-6 shrink-0 place-items-center rounded-md bg-warning/20 text-warning">
            <Eye className="h-3.5 w-3.5" />
          </span>
          <span className="min-w-0 text-fg-muted">
            Viewing as <strong className="font-semibold text-fg">{me.data.fullName}</strong>{" "}
            <span className="capitalize">({me.data.role})</span>
            <span> — impersonated by {me.data.impersonatorName} · read-only</span>
          </span>
        </span>
        <button
          onClick={exit}
          disabled={stop.isPending}
          className="inline-flex h-7 items-center gap-1.5 rounded-md bg-warning/20 px-2.5 text-[12px] font-semibold text-warning ring-1 ring-inset ring-warning/40 transition-colors hover:bg-warning/30 disabled:opacity-60"
        >
          <LogOut className="h-3.5 w-3.5" /> Stop &amp; return to admin
        </button>
      </div>
    </div>
  );
}
